/**
 * 2025 state income-tax models (TAX-03/TAX-18). Each figure cites its source;
 * a year whose official schedule is not yet published is marked
 * `provisional` with the assumption stated.
 */

import type { FilingStatus } from '../../types.js';
import type { FlatStateTaxModel, NoIndividualIncomeTaxModel, ProgressiveStateTaxModel, StateTaxBracket } from './types.js';

const CALIFORNIA_STANDARD_DEDUCTIONS: Record<FilingStatus, number> = {
  single: 5_706,
  marriedFilingJointly: 11_412,
  marriedFilingSeparately: 5_706,
  headOfHousehold: 11_412,
};

const CALIFORNIA_SCHEDULE_X: readonly StateTaxBracket[] = [
  { minimum: 0, maximum: 11_079, rate: 0.01, baseTax: 0 },
  { minimum: 11_079, maximum: 26_264, rate: 0.02, baseTax: 110.79 },
  { minimum: 26_264, maximum: 41_452, rate: 0.04, baseTax: 414.49 },
  { minimum: 41_452, maximum: 57_542, rate: 0.06, baseTax: 1_022.01 },
  { minimum: 57_542, maximum: 72_724, rate: 0.08, baseTax: 1_987.41 },
  { minimum: 72_724, maximum: 371_479, rate: 0.093, baseTax: 3_201.97 },
  { minimum: 371_479, maximum: 445_771, rate: 0.103, baseTax: 30_986.19 },
  { minimum: 445_771, maximum: 742_953, rate: 0.113, baseTax: 38_638.27 },
  { minimum: 742_953, maximum: null, rate: 0.123, baseTax: 72_219.84 },
];

const CALIFORNIA_SCHEDULE_Y: readonly StateTaxBracket[] = [
  { minimum: 0, maximum: 22_158, rate: 0.01, baseTax: 0 },
  { minimum: 22_158, maximum: 52_528, rate: 0.02, baseTax: 221.58 },
  { minimum: 52_528, maximum: 82_904, rate: 0.04, baseTax: 828.98 },
  { minimum: 82_904, maximum: 115_084, rate: 0.06, baseTax: 2_044.02 },
  { minimum: 115_084, maximum: 145_448, rate: 0.08, baseTax: 3_974.82 },
  { minimum: 145_448, maximum: 742_958, rate: 0.093, baseTax: 6_403.94 },
  { minimum: 742_958, maximum: 891_542, rate: 0.103, baseTax: 61_972.37 },
  { minimum: 891_542, maximum: 1_485_906, rate: 0.113, baseTax: 77_276.52 },
  { minimum: 1_485_906, maximum: null, rate: 0.123, baseTax: 144_439.65 },
];

const CALIFORNIA_SCHEDULE_Z: readonly StateTaxBracket[] = [
  { minimum: 0, maximum: 22_173, rate: 0.01, baseTax: 0 },
  { minimum: 22_173, maximum: 52_530, rate: 0.02, baseTax: 221.73 },
  { minimum: 52_530, maximum: 67_716, rate: 0.04, baseTax: 828.87 },
  { minimum: 67_716, maximum: 83_805, rate: 0.06, baseTax: 1_436.31 },
  { minimum: 83_805, maximum: 98_990, rate: 0.08, baseTax: 2_401.65 },
  { minimum: 98_990, maximum: 505_208, rate: 0.093, baseTax: 3_616.45 },
  { minimum: 505_208, maximum: 606_251, rate: 0.103, baseTax: 41_394.72 },
  { minimum: 606_251, maximum: 1_010_417, rate: 0.113, baseTax: 51_802.15 },
  { minimum: 1_010_417, maximum: null, rate: 0.123, baseTax: 97_472.91 },
];

export const CALIFORNIA_2025: ProgressiveStateTaxModel = {
  status: 'supported',
  stateCode: 'CA',
  stateName: 'California',
  taxYear: 2025,
  modelVersion: 'CA-2025-FTB',
  startingPoint: 'federalAdjustedGrossIncome',
  standardDeductions: CALIFORNIA_STANDARD_DEDUCTIONS,
  brackets: {
    single: CALIFORNIA_SCHEDULE_X,
    marriedFilingJointly: CALIFORNIA_SCHEDULE_Y,
    marriedFilingSeparately: CALIFORNIA_SCHEDULE_X,
    headOfHousehold: CALIFORNIA_SCHEDULE_Z,
  },
  surtax: {
    threshold: 1_000_000,
    rate: 0.01,
    name: 'Behavioral Health Services Tax',
  },
  sources: [
    {
      title: '2025 California Form 540 Tax Rate Schedules',
      url: 'https://www.ftb.ca.gov/forms/2025/2025-540-tax-rate-schedules.pdf',
    },
    {
      title: '2025 California Form 540 Booklet',
      url: 'https://www.ftb.ca.gov/forms/2025/2025-540-booklet.html',
    },
  ],
};

export const COLORADO_2025: FlatStateTaxModel = {
  status: 'supported',
  stateCode: 'CO',
  stateName: 'Colorado',
  taxYear: 2025,
  modelVersion: 'CO-2025-DR-0104',
  startingPoint: 'federalTaxableIncome',
  rate: 0.044,
  sources: [
    {
      title: '2025 Colorado Individual Income Tax Booklet (DR 0104)',
      url: 'https://tax.colorado.gov/sites/tax/files/documents/Book104_2025.pdf',
    },
    {
      title: '2025 Colorado Subtractions Schedule (DR 0104AD)',
      url: 'https://tax.colorado.gov/sites/tax/files/documents/DR0104AD_2025.pdf',
    },
  ],
};

export const TEXAS_2025: NoIndividualIncomeTaxModel = {
  status: 'noIndividualIncomeTax',
  stateCode: 'TX',
  stateName: 'Texas',
  taxYear: 2025,
  modelVersion: 'TX-2025-NO-INDIVIDUAL-INCOME-TAX',
  sources: [
    {
      title: 'Texas Comptroller Sources of Revenue Field Guide',
      url: 'https://comptroller.texas.gov/transparency/revenue/docs/96-1774.pdf',
    },
  ],
};
